import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiBriefcase, FiAlertCircle, FiCheck, FiInfo } from 'react-icons/fi';
import { loadTable, getTableData } from '../../db-viewer/data/dataAccess';
import SearchableDropdown, { DropdownOption } from '../../../components/ui/SearchableDropdown';
import { addNotification } from '../../../lib/notifications';
import { setFormDirty, registerSaveHandler } from '../../../lib/unsavedChanges';
import { getBusinessTypes, setBusinessType, BusiCategory, BusiType } from '../coaTemplateApi';
import './GLChartOfAccountsTab.css';

// =============================================
// Type of Business tab — pick a business's INDUSTRY type (not its legal form).
// The category is auto-derived from the chosen type. Writes user_businesses.industry_type_id.
// Business type only applies to business entities, so this tab lists businesses only.
// =============================================
interface Props {
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
}

interface BizEntity {
  owner_type: 'business' | 'client_business';
  business_id: number;
  name: string;
  industry_type_id: number | null;
}

function resolveAccountOwnerId(users: Record<string, any>[]): number {
  const owner = users.find((u: any) => u.owner_id == null); // eslint-disable-line eqeqeq
  return owner ? Number(owner.user_id) : 0;
}

function currentUserId(): number | null {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const s = JSON.parse(raw);
    const id = Number(s.id || s.user_id);
    return id || null;
  } catch { return null; }
}

const TypeOfBusinessTab: React.FC<Props> = ({ viewMode = 'owner', selectedClientId = null }) => {
  const [users, setUsers] = useState<Record<string, any>[]>([]);
  const [businesses, setBusinesses] = useState<Record<string, any>[]>([]);
  const [categories, setCategories] = useState<BusiCategory[]>([]);
  const [types, setTypes] = useState<BusiType[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [selectedBizId, setSelectedBizId] = useState<number | null>(null);
  const [selectedTypeId, setSelectedTypeId] = useState<string>('');
  const [savedMsg, setSavedMsg] = useState('');
  const [saving, setSaving] = useState(false);

  // ---- Load reference data + entities ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [usersRows, bizRows] = await Promise.all([
          loadTable('users').catch(() => getTableData('users')),
          loadTable('user_businesses').catch(() => getTableData('user_businesses')),
        ]);
        const ref = await getBusinessTypes();
        if (cancelled) return;
        setUsers(usersRows);
        setBusinesses(bizRows);
        setCategories(ref.categories);
        setTypes(ref.types);
        setLoaded(true);
      } catch (e: any) {
        if (cancelled) return;
        setLoadError(e?.message || 'Could not load business types.');
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const accountOwnerId = useMemo(() => resolveAccountOwnerId(users), [users]);

  // ---- Business entities for this mode ----
  const bizEntities = useMemo<BizEntity[]>(() => {
    if (!accountOwnerId) return [];
    if (viewMode === 'clients') {
      if (!selectedClientId) return [];
      return businesses
        .filter((b: any) => b.owner_id == selectedClientId && b.is_deleted != 1 && b.is_client_business == 1) // eslint-disable-line eqeqeq
        .map((b: any) => ({
          owner_type: 'client_business' as const,
          business_id: Number(b.business_id),
          name: b.busi_legal_name || b.busi_operating_name || 'Client Business',
          industry_type_id: b.industry_type_id == null || b.industry_type_id === '' ? null : Number(b.industry_type_id),
        }));
    }
    return businesses
      .filter((b: any) => b.owner_id == accountOwnerId && b.is_deleted != 1 && b.is_client_business != 1) // eslint-disable-line eqeqeq
      .map((b: any) => ({
        owner_type: 'business' as const,
        business_id: Number(b.business_id),
        name: b.busi_legal_name || b.busi_operating_name || 'Business',
        industry_type_id: b.industry_type_id == null || b.industry_type_id === '' ? null : Number(b.industry_type_id),
      }));
  }, [businesses, accountOwnerId, viewMode, selectedClientId]);

  const selectedBiz = useMemo(
    () => bizEntities.find(b => b.business_id === selectedBizId) || null,
    [bizEntities, selectedBizId]);

  // Auto-select the only business, and seed the type from its stored value.
  useEffect(() => {
    if (bizEntities.length === 1 && selectedBizId == null) {
      setSelectedBizId(bizEntities[0].business_id);
    }
  }, [bizEntities, selectedBizId]);

  useEffect(() => {
    if (selectedBiz) setSelectedTypeId(selectedBiz.industry_type_id ? String(selectedBiz.industry_type_id) : '');
  }, [selectedBiz]);

  // ---- Type dropdown options grouped by category ----
  const typeOptions = useMemo<DropdownOption[]>(() => {
    const catName = new Map(categories.map(c => [c.busi_category_id, c.category_name]));
    return types.map(t => ({
      value: String(t.busi_type_id),
      label: t.type_name,
      group: catName.get(t.busi_category_id) || 'Other',
    }));
  }, [types, categories]);

  const selectedType = useMemo(
    () => types.find(t => String(t.busi_type_id) === selectedTypeId) || null,
    [types, selectedTypeId]);

  const derivedCategory = useMemo(
    () => selectedType ? categories.find(c => c.busi_category_id === selectedType.busi_category_id) || null : null,
    [selectedType, categories]);

  const isChange = !!(selectedBiz && selectedBiz.industry_type_id && selectedBiz.industry_type_id !== Number(selectedTypeId));
  const isDirty = !!(selectedBiz && selectedTypeId && Number(selectedTypeId) !== (selectedBiz.industry_type_id || 0));

  useEffect(() => { setFormDirty(isDirty); }, [isDirty]);

  // ---- Save ----
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!selectedBiz || !selectedTypeId) return false;
    setSaving(true);
    try {
      await setBusinessType({
        owner_type: selectedBiz.owner_type,
        owner_id: selectedBiz.business_id,
        busi_type_id: Number(selectedTypeId),
      });
      // Reflect locally so the tab + Setup GL Accounts see the new value immediately.
      setBusinesses(prev => prev.map((b: any) =>
        Number(b.business_id) === selectedBiz.business_id ? { ...b, industry_type_id: Number(selectedTypeId) } : b));
      setFormDirty(false);
      setSavedMsg(`Business type saved: ${selectedType?.type_name || ''}`);
      setTimeout(() => setSavedMsg(''), 3500);

      // Notify the account owner if a sub-user made the change.
      const uid = currentUserId();
      if (uid && uid !== accountOwnerId && accountOwnerId) {
        const actor = users.find((u: any) => Number(u.user_id) === uid);
        const actorName = actor ? `${actor.first_name || ''} ${actor.last_name || ''}`.trim() : 'A user';
        addNotification({
          account_owner_id: accountOwnerId,
          type: 'busi_type_set',
          title: 'Business type set',
          message: `${actorName} set "${selectedBiz.name}" as ${selectedType?.type_name}.`,
          actor_user_id: uid,
          actor_name: actorName,
        });
      }
      return true;
    } catch (e: any) {
      setSavedMsg('');
      setLoadError(e?.message || 'Failed to save business type.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [selectedBiz, selectedTypeId, selectedType, accountOwnerId, users]);

  useEffect(() => {
    registerSaveHandler(() => handleSave());
    return () => registerSaveHandler(null);
  }, [handleSave]);

  // ---- Render ----
  if (!loaded) {
    return <div className="coa-tab"><div className="coa-empty"><p>Loading business types…</p></div></div>;
  }

  if (viewMode === 'clients' && !selectedClientId) {
    return <div className="coa-tab"><div className="coa-empty"><p>Select a client first.</p></div></div>;
  }

  return (
    <div className="coa-tab">
      <div className="coa-info-banner">
        Choose the <strong>type of business</strong> for each business. We use it to suggest a starting
        Chart of Accounts. The industry <strong>category is filled in automatically</strong> from your choice.
      </div>

      {loadError && (
        <div className="coa-field-error" style={{ marginBottom: 12 }}>
          <FiAlertCircle size={14} /> {loadError}
        </div>
      )}

      {bizEntities.length === 0 ? (
        <div className="coa-empty">
          <div className="coa-empty-icon"><FiBriefcase size={40} /></div>
          <p>No businesses found for this {viewMode === 'clients' ? 'client' : 'account'}.
            Add a business in <strong>Family &amp; Businesses</strong> first.</p>
        </div>
      ) : (
        <div className="coa-form-card">
          {/* Business picker (only when more than one) */}
          {bizEntities.length > 1 && (
            <div className="coa-field-group">
              <label className="coa-field-label">Business</label>
              <SearchableDropdown
                options={bizEntities.map(b => ({ value: String(b.business_id), label: b.name }))}
                value={selectedBizId ? String(selectedBizId) : ''}
                onChange={(v) => setSelectedBizId(v ? Number(v) : null)}
                placeholder="-- Select a business --"
              />
            </div>
          )}

          {selectedBiz && (
            <>
              <div className="coa-field-group">
                <label className="coa-field-label">
                  Type of Business <span className="coa-required">*</span>
                </label>
                <SearchableDropdown
                  options={typeOptions}
                  value={selectedTypeId}
                  onChange={setSelectedTypeId}
                  placeholder="-- Choose your business type --"
                />
                {isChange && (
                  <span className="coa-field-hint" style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, color: 'var(--amber-600, #b45309)' }}>
                    <FiInfo size={13} />
                    Changing the type won't touch accounts you've already created — it just re-offers the new type's accounts in Setup GL Accounts.
                  </span>
                )}
              </div>

              {/* Auto-derived category (read-only) */}
              <div className="coa-field-group">
                <label className="coa-field-label">Industry Category (auto-detected)</label>
                <input
                  className="coa-search-input"
                  type="text"
                  readOnly
                  value={derivedCategory ? derivedCategory.category_name : ''}
                  placeholder="Pick a business type above"
                  style={{ background: '#f5f5f5', color: '#555' }}
                />
              </div>

              <div className="coa-action-bar" style={{ marginTop: 8 }}>
                <button
                  className="coa-btn-primary"
                  disabled={!selectedTypeId || !isDirty || saving}
                  onClick={handleSave}
                >
                  <FiCheck size={15} /> {saving ? 'Saving…' : (selectedBiz.industry_type_id ? 'Update business type' : 'Save business type')}
                </button>
                {savedMsg && <span className="coa-saved-msg" style={{ marginLeft: 12 }}>{savedMsg}</span>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TypeOfBusinessTab;
